import React, { useState } from 'react';
import { Schemas } from '../lib/schemas';
import { fetchJson } from '../lib/http';
import { Layout } from '../components/Layout';
import { ErrorAlert } from '../components/ErrorAlert';

export function ActivatePage() {
	const [customerId, setCustomerId] = useState('');
	const [postalCode, setPostalCode] = useState('');
	const [email, setEmail] = useState('');
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState<string | null>(null);
	const [error, setError] = useState<string>('');

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(''); setSuccess(null);
		const parse = Schemas.activate.safeParse({ customer_id: customerId.trim(), postal_code: postalCode.trim(), email: email.trim() });
		if (!parse.success) {
			setError('Please provide valid Customer ID, ZIP, and email.');
			return;
		}
		setLoading(true);
		try {
			await fetchJson('/portal/activate', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(parse.data),
			});
			setSuccess('If the information is valid, we will email you a link to complete setup.');
		} catch (e: any) {
			if (e?.status === 429) setError('Too many attempts. Please try again in a few minutes.');
			else setError('We could not process your request. Please try again.');
		} finally {
			setLoading(false);
		}
	}

	return (
		<Layout>
			<div className="rounded-2xl border border-gray-200 bg-white shadow-sm elev-1 p-6 max-w-lg">
				<h1 className="text-2xl font-extrabold text-gray-900">Activate your account</h1>
				<p className="mt-2 text-sm text-gray-700">Enter your Customer ID, ZIP code, and email to get a one-time link.</p>
				<form onSubmit={onSubmit} className="mt-6 space-y-4" aria-describedby="form-help">
					<div>
						<label htmlFor="customerId" className="block text-sm font-semibold text-gray-700">Customer ID</label>
						<input id="customerId" name="customerId" autoComplete="off" required value={customerId} onChange={e => setCustomerId(e.target.value)}
							className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2" />
					</div>
					<div>
						<label htmlFor="postalCode" className="block text-sm font-semibold text-gray-700">ZIP code</label>
						<input id="postalCode" name="postalCode" autoComplete="postal-code" required value={postalCode} onChange={e => setPostalCode(e.target.value)}
							className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2" />
					</div>
					<div>
						<label htmlFor="email" className="block text-sm font-semibold text-gray-700">Email</label>
						<input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)}
							className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2" />
					</div>
					<ErrorAlert message={error} />
					{success && (
						<div role="status" className="rounded-xl border border-green-200 bg-green-50 text-green-800 px-4 py-3 text-sm">
							{success}
						</div>
					)}
					<div className="pt-2">
						<button type="submit" disabled={loading} className="rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white hover:bg-sedra-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra">
							{loading ? 'Submitting…' : 'Submit'}
						</button>
					</div>
					<p id="form-help" className="sr-only">We will display a generic confirmation. No personal information will be revealed.</p>
				</form>
			</div>
		</Layout>
	);
} 